/**
 * `parseJwtSub` — shared by both VoIP token registration paths.
 *
 * It lived in index.ts, and web-voip-registration.ts imported it back from
 * there. index.ts reaches web-voip-registration.ts through a *dynamic* import
 * (deliberate — it keeps the VAPID key and the web registration out of the
 * app-shell entry chunk), so that back-import closed a cycle:
 *
 *   index.ts -> (dynamic) web-voip-registration.ts -> (static) index.ts
 *
 * `depcruise no-circular` fails on it, and that check runs only in the
 * Dockerfile lint-quality stage — not in the PR gate — so it broke the deploy
 * build rather than the pull request that introduced it (#3032).
 *
 * A leaf module both sides import breaks the cycle and keeps the lazy chunk.
 */
/**
 * Parse the JWT payload and return the `sub` claim (user_id).
 * Standard 3-part base64url JWT — no signature verification needed here
 * (this is for building the request body only; the server validates the JWT).
 * Returns null on any parse error (malformed token → registration skipped).
 */
export function parseJwtSub(jwt: string): string | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 → decode
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(payload);
    const claims = JSON.parse(json) as Record<string, unknown>;
    const sub = claims['sub'];
    return typeof sub === 'string' && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}
